import { Link, useParams } from 'react-router-dom';
import { routes } from '../routes/routes';
import useApi from '../api';
import { useQuery } from 'react-query';
import queryConfig from '../util/queryConfig';
import Throbber from '../components/throbber/Throbber';
import Error from '../components/error/Error';
import { QueryKey } from '../constants/queryKey';

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const Section = ({ title, children }) => (
    <div style={{ marginBottom: '24px' }}>
        <h3 className="heading heading--sm" style={{ marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' }}>{title}</h3>
        {children}
    </div>
);

const Row = ({ label, value }) => (
    <div className="table__row">
        <div className="table__label">{label}</div>
        <div className="table__value">{value || <span style={{ color: '#999' }}>—</span>}</div>
    </div>
);

const Badge = ({ label, active }) => (
    <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        marginRight: '6px',
        borderRadius: '3px',
        fontSize: '12px',
        background: active ? '#e6f4ea' : '#f4e6e6',
        color: active ? '#1e7e34' : '#999',
    }}>
        {label}
    </span>
);

const ScenarioView = () => {
    const { slug } = useParams();
    const { getScenario } = useApi();
    const { isLoading, error, data } = useQuery([QueryKey.Scenarios, { slug }], async () => {
        const response = await getScenario(slug);
        return response.data;
    }, queryConfig);

    return (
        <div className="view">
            {isLoading ? (
                <Throbber title="Loading scenario" />
            ) : error ? (
                <Error msg="Something went wrong loading the scenario. Please try again later." />
            ) : (
                <>
                    <h1 className="heading">
                        {data?.properties?.name || data?.scenario_id}
                    </h1>
                    {data?.properties?.remark && (
                        <p style={{ color: '#666', marginBottom: '20px' }}>{data.properties.remark}</p>
                    )}

                    <div className="table">

                        <Section title="Render Stats">
                            <Row label="Total jobs" value={data?.stats?.total_jobs} />
                            <Row label="Completed" value={data?.stats?.completed} />
                            <Row label="Failed" value={data?.stats?.failed} />
                            <Row label="Pending" value={data?.stats?.pending} />
                        </Section>

                        <Section title="Ingestion">
                            {data?.ingest ? (
                                <>
                                    <Row label="Type" value="Email inbound" />
                                    <Row label="Plugin" value={data.ingest.ingest?.email?.plugin} />
                                    <Row label="Enabled" value={data.ingest.ingest?.email?.enabled === false ? 'No' : 'Yes'} />
                                </>
                            ) : (
                                <Row label="Type" value="CRM polling (cron)" />
                            )}
                        </Section>

                        <Section title="Postprocess">
                            {data?.postprocess?.postprocess?.map((action, i) => {
                                if (action.Mail) {
                                    return (
                                        <div key={i} style={{ marginBottom: '12px' }}>
                                            <Row label="Action" value="Email notification" />
                                            <Row label="From" value={action.Mail.from} />
                                            <Row label="To" value={action.Mail.to} />
                                            <Row label="Subject" value={action.Mail.subject} />
                                            {action.Mail.remark && <Row label="Remark" value={action.Mail.remark} />}
                                        </div>
                                    );
                                }
                                if (action.Webhook) {
                                    return (
                                        <div key={i}>
                                            <Row label="Action" value="Webhook (CRM callback)" />
                                        </div>
                                    );
                                }
                                return null;
                            }) || <Row label="Actions" value="None configured" />}
                        </Section>

                        <Section title="Render Settings">
                            <Row label="Resolution" value={data?.settings ? `${data.settings.resolution} (${data.settings.width}x${data.settings.height})` : null} />
                            <Row label="Framerate" value={data?.settings?.framerate ? `${data.settings.framerate} fps` : null} />
                        </Section>

                        <Section title="Scenario Template">
                            <Row label="Scenes" value={data?.scenario?.sceneCount} />
                            <div className="table__row">
                                <div className="table__label">Features</div>
                                <div className="table__value">
                                    <Badge label="Subtitles" active={data?.scenario?.hasSubtitles} />
                                    <Badge label="Overlays" active={data?.scenario?.hasOverlays} />
                                    <Badge label="Buttons" active={data?.scenario?.hasButtons} />
                                </div>
                            </div>
                        </Section>

                        <Section title="Assets">
                            <Row label="Files" value={data?.assets?.count} />
                            <Row label="Total size" value={formatBytes(data?.assets?.totalSize)} />
                        </Section>

                    </div>

                    <Link to={routes.scenarios} className="button" style={{ marginTop: '20px', display: 'inline-block' }}>
                        Back to scenarios
                    </Link>
                </>
            )}
        </div>
    );
};

export default ScenarioView;
